/**
 * featuredImageGate.ts — WS-6 pre-publish INFO row: "this article has no
 * picture for social shares."
 *
 * Carl-approved scope: an info-tier (never blocking, no confirmation
 * required) row that fires only when EVERY og-image source visible to the
 * frontend is empty:
 *   - no explicit editor OG-image override (`og_image_url`, set via the
 *     rail's "Use as OG image" button — UniversalEditorRailMount.tsx
 *     `handleSetOgImage`), AND
 *   - no WordPress media id designated as the featured image in the editor
 *     (`featured_image_id` — set via "Set as hero" / the generated-hero
 *     flow, UniversalEditorRailMount.tsx `handleSetHeroImage` /
 *     `editorAIService.updateFeaturedImageId`), AND
 *   - no generation-time featured image placeholder in
 *     `generation_metadata.featured_image_url` (or the legacy
 *     `hero_image_placeholder_url` key) — the same two keys
 *     backend/api/services/content_service.py `resolve_featured_image_source`
 *     reads.
 *
 * Scope note (honest by design, per Carl): a WordPress post can carry a
 * featured image assigned OUTSIDE this app (e.g. set directly in wp-admin).
 * That is not knowable client-side, so the message says "if the WordPress
 * post has no featured media either" rather than asserting the final
 * published state — see backend wordpress_service._build_extended_seo_meta,
 * which falls back og:image to og_image_url → media_source_url (the WP
 * featured media) when nothing else is set.
 */
import type { MetadataViolation } from './metadataCriteria';

export interface FeaturedImageGateInput {
  /** Editor "Use as OG image" override. */
  ogImageUrl?: string | null;
  /** WordPress media id designated as the featured image in the editor. */
  featuredImageId?: number | null;
  /** content.generation_metadata — read for the featured-image keys only. */
  generationMetadata?: Record<string, unknown> | null;
}

/** Mirrors backend `resolve_featured_image_source` key order (canonical, then legacy). */
const GENERATION_FEATURED_IMAGE_KEYS = ['featured_image_url', 'hero_image_placeholder_url'] as const;

function readGenerationFeaturedImageUrl(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  for (const key of GENERATION_FEATURED_IMAGE_KEYS) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

const hasText = (v: string | null | undefined): boolean => typeof v === 'string' && v.trim().length > 0;
const hasId = (v: number | null | undefined): boolean =>
  typeof v === 'number' && Number.isFinite(v) && v > 0;

/**
 * Build the `featured_image_missing` info row, or `[]` when any og-image
 * source is populated. Pure & synchronous — never throws.
 */
export function featuredImageMissingViolations(input: FeaturedImageGateInput): MetadataViolation[] {
  const generationUrl = readGenerationFeaturedImageUrl(input.generationMetadata);
  if (hasText(input.ogImageUrl) || hasId(input.featuredImageId) || hasText(generationUrl)) {
    return [];
  }
  return [
    {
      ruleId: 'featured_image_missing',
      field: 'og_image',
      tier: 'info',
      severity: 'info',
      category: 'contract',
      message:
        'No featured or OG image is set in the editor yet — if the WordPress post has no featured media either, social shares (Facebook, LinkedIn, X) will render with no picture. Set a hero image or use "Use as OG image" on an image to fix this.',
      sourceNote: 'info-tier (WS-6 og-image gate row)',
    },
  ];
}
